/**
 * Centralized Environment Configuration
 * 
 * Single source of truth for all environment variables.
 * Validates required vars on import and provides typed access.
 */

// Environment detection
export const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 
  (import.meta.env.PROD ? "production" : "development");

export const isProduction = ENVIRONMENT === "production";
export const isDevelopment = ENVIRONMENT === "development";
export const isStaging = ENVIRONMENT === "staging";

// Supabase configuration
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
export const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
export const SUPABASE_PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID;

// AI Gateway configuration
export const AI_GATEWAY_URL = import.meta.env.VITE_AI_GATEWAY_URL || 
  "https://ai.gateway.lovable.dev/v1/chat/completions";
export const AI_MODEL = import.meta.env.VITE_AI_MODEL || "google/gemini-2.5-flash";

// Feature toggles
export const ENABLE_INTERNAL_TOOLS = import.meta.env.VITE_ENABLE_INTERNAL_TOOLS === "true";

// App metadata
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || "1.0.0";

/**
 * Required environment variables by environment
 */
const REQUIRED_VARS: Record<string, string[]> = {
  production: [
    "VITE_SUPABASE_URL",
    "VITE_SUPABASE_PUBLISHABLE_KEY",
  ],
  staging: [
    "VITE_SUPABASE_URL",
    "VITE_SUPABASE_PUBLISHABLE_KEY",
  ],
  development: [], // More lenient in dev
};

/**
 * Validates that required environment variables are set.
 * Throws in production, warns in development.
 */
export function validateEnvironment(): { valid: boolean; missing: string[] } {
  const requiredVars = REQUIRED_VARS[ENVIRONMENT] || [];
  const missing = requiredVars.filter(v => !import.meta.env[v]);
  
  if (missing.length > 0) {
    const message = `Missing required environment variables: ${missing.join(", ")}`;
    if (isProduction) {
      throw new Error(message);
    } else {
      console.warn(`[ENV WARNING] ${message}`);
    }
  }
  
  return { valid: missing.length === 0, missing };
}

/**
 * Get all environment info for debugging (non-sensitive only)
 */
export function getEnvironmentInfo(): Record<string, string | boolean> {
  return {
    environment: ENVIRONMENT,
    isProduction,
    isDevelopment,
    isStaging,
    supabaseConfigured: !!SUPABASE_URL,
    internalToolsEnabled: ENABLE_INTERNAL_TOOLS,
    appVersion: APP_VERSION,
  };
}
